import React, { Component } from 'react';
import { RootState } from '../../redux/store';
import { DashboardState } from '../../redux/reducers/DashboardReducer';
import { changeDashboard, changeEditMode, setDashboardResponse } from '../../redux/actions/DashboardActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomTable from '../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import ItemStatusComponent from '../ItemStatusComponent';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentName from '../../constant/ComponentName';
import ItemToolsMenu from '../ItemToolsMenu';
import FilterUtil from '../../util/FilterUtil';
import ValueFactory from '../../util/ValueFactory';
import _ from 'lodash';
import DashboardService from '../../service/DashboardService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { query } from '@trlogic/client-js';
import { changePageFilter } from '../../redux/actions/PageActions';
import Filter = query.Filter;
import empty = query.empty;
import LanguageUtil from '../../util/LanguageUtil';

class DashboardRulePerformanceComponent extends Component<Props, State> {
  public state = new State();
  private readonly dashboardService: DashboardService = ServiceContainer.getInstance().get(ServiceNames.DASHBOARD);

  render() {
    const {
      dashboard: { response },
    } = this.props;
    const { filter } = this.state;

    return (
      <div className={'flex flex-col w-full h-full px-4 py-5'}>
        <ItemToolsMenu importVisible={false} onChangeFilter={this.onChangeFilter} filter={filter} />
        <CustomTable columns={this.getColumns()} value={response?.rulePerformance} />
      </div>
    );
  }

  public async componentDidMount() {
    await this.prepareFilterFields();
    await this.getRulePerformance();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (this.state.filter != prevState.filter) {
      await this.getRulePerformance();
    }
  }

  private async getRulePerformance() {
    const {
      dashboard: { dashboard, response },
      setDashboardResponse,
    } = this.props;
    const { filter } = this.state;
    const rulePerformance = await this.dashboardService.getRulePerformance(dashboard.id as string, filter as any);
    if (rulePerformance && response) {
      response.rulePerformance = rulePerformance;
      setDashboardResponse(response);
    }
  }

  private onChangeFilter = async (_filter: Filter) => {
    const { filter } = this.state;
    const isSame = _.isEqual(filter, _filter);
    if (isSame) return;
    this.setState({ filter: _filter });
  };

  public prepareFilterFields = async () => {
    const { changePageFilter } = this.props;
    const filterFields = await FilterUtil.getFields(ComponentName.DASHBOARD);
    changePageFilter({
      filter: ValueFactory.initializeFilter(),
      items: filterFields,
    });
  };

  private getColumns = () => {
    const columns: ColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: `pages.dashboard.table.name`, defaultMessage: 'Name' }),
      },
      {
        field: 'status',
        header: LanguageUtil.get({ id: `pages.dashboard.table.status`, defaultMessage: 'Status' }),
        body: (data: any) => {
          return <ItemStatusComponent status={data.status || 'DRAFT'} />;
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: `pages.dashboard.table.description`, defaultMessage: 'Description' }),
        body: (data: any) => {
          return <CustomTableContentWrapper text={data.description || ''} />;
        },
      },
      {
        field: 'score',
        header: LanguageUtil.get({ id: `pages.dashboard.table.score`, defaultMessage: 'Description' }),
        body: (data: any) => {
          const score = data.score || 0;
          return (
            <div
              className={`flex flex-col justify-center items-center ${score == 0 ? 'bg-[#DBF8E5] text-[#3F9E2C]' : score == 100 ? 'bg-[#FFF1F3] text-[#D2332F]' : 'bg-[#FCF1BB] text-[#C57500]'} w-[40px] text-xs 2xl:text-md rounded-[4px] h-[22px]`}
            >
              <CustomTableContentWrapper text={score} />
            </div>
          );
        },
      },
      {
        field: 'hits',
        header: LanguageUtil.get({ id: `pages.dashboard.table.hits`, defaultMessage: 'Description' }),
        body: (data: any) => {
          return <CustomTableContentWrapper text={data.hits || ''} />;
        },
      },
      {
        field: 'fraud',
        header: LanguageUtil.get({ id: `pages.dashboard.table.fraud`, defaultMessage: 'Description' }),
        body: (data: any) => {
          return <CustomTableContentWrapper text={data.fraud || ''} />;
        },
      },
      {
        field: 'fraudRatio',
        header: LanguageUtil.get({ id: `pages.dashboard.table.fraudRatio`, defaultMessage: 'Description' }),
        body: (data: any) => {
          if (!data.fraudRatio) return 'N/A';
          return <CustomTableContentWrapper text={'%' + data.fraudRatio || ''} />;
        },
      },
      {
        field: 'hitsRatio',
        header: LanguageUtil.get({ id: `pages.dashboard.table.hitsRatio`, defaultMessage: 'Description' }),
        body: (data: any) => {
          if (!data.hitsRatio) return 'N/A';
          return <CustomTableContentWrapper text={'%' + data.hitsRatio || ''} />;
        },
      },
    ];

    return columns;
  };
}

class State {
  filter: Filter | undefined = empty();
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  dashboard: state.dashboard as DashboardState,
});
const mapDispatchToProps = { changeDashboard, changeEditMode, setDashboardResponse, changePageFilter };

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DashboardRulePerformanceComponent);
